import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import {
  ArrowRight, Building2, Briefcase, DollarSign, Users,
  TrendingUp, Shield, Clock, Zap, Globe, CheckCircle,
  Percent, BarChart3, Headphones, FileText
} from 'lucide-react';

const JoinUsPage = () => {
  const benefits = [
    { icon: Percent, title: 'Special Discounts', desc: 'Get exclusive partner discounts on visa fees for all your client applications.' },
    { icon: DollarSign, title: 'Earn Commission', desc: 'Earn a commission on every completed application you submit for your clients.' },
    { icon: BarChart3, title: 'Partner Dashboard', desc: 'Track all client applications, revenue, and commissions from a single dashboard.' },
    { icon: Users, title: 'Bulk Submissions', desc: 'Submit multiple visa applications at once for corporate groups or tour packages.' },
    { icon: Clock, title: 'Priority Processing', desc: 'Partner applications get prioritized in our processing queue for faster turnaround.' },
    { icon: Headphones, title: 'Dedicated Support', desc: 'Get a dedicated account manager and priority support for all queries.' },
  ];

  const steps = [
    { num: '01', title: 'Register', desc: 'Fill out the partner registration form with your business details.' },
    { num: '02', title: 'Get Approved', desc: 'Our team reviews your application and approves your partner account.' },
    { num: '03', title: 'Start Earning', desc: 'Submit applications for clients and earn commissions on every booking.' },
  ];

  const partnerTypes = [
    {
      icon: Briefcase,
      title: 'Travel Agents',
      desc: 'For independent travel agents and agencies who process visa applications for their clients.',
      features: ['Submit on behalf of clients', 'Client management dashboard', 'Volume-based discounts', 'Marketing materials'],
    },
    {
      icon: Building2,
      title: 'Corporate Partners',
      desc: 'For businesses that need to process employee visas for international travel and assignments.',
      features: ['Bulk employee submissions', 'Company-wide tracking', 'Centralized billing', 'Compliance reports'],
    },
  ];

  return (
    <div className="min-h-screen bg-[#F5F0E6]">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-28 pb-16 px-4" data-testid="joinus-hero">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#FF5A36]/10 text-[#FF5A36] px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Building2 className="w-4 h-4" /> Partner Program
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0A1E40] mb-4 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Grow Your Business<br />
            <span className="text-[#FF5A36]">With EasyMyVisa</span>
          </h1>
          <p className="text-[#4A5568] text-lg md:text-xl max-w-2xl mx-auto mb-8">
            Join our partner network of travel agents and corporate partners. Earn commissions, get special pricing, and manage all client visas from one place.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/partner/register">
              <Button className="bg-[#FF5A36] hover:bg-[#E0492B] text-white text-lg px-10 py-6 rounded-full font-semibold" data-testid="joinus-register-btn">
                Become a Partner <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="text-lg px-10 py-6 rounded-full border-[#D1CBBF] text-[#0A1E40]" data-testid="joinus-contact-btn">
                Talk to Sales
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="pb-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: '500+', label: 'Active Partners' },
              { value: '50K+', label: 'Applications Processed' },
              { value: '99.3%', label: 'Approval Rate' },
              { value: '30+', label: 'Countries Covered' },
            ].map((stat, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#D1CBBF] p-5 text-center" data-testid={`stat-${i}`}>
                <p className="text-3xl font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>{stat.value}</p>
                <p className="text-sm text-[#4A5568] mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-16 px-4 bg-white" data-testid="partner-types">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0A1E40] text-center mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Who Can Join?
          </h2>
          <p className="text-[#4A5568] text-center mb-10 max-w-xl mx-auto">Choose the partner type that best fits your business model.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {partnerTypes.map((type, i) => (
              <div key={i} className="rounded-2xl border-2 border-[#E8E2D6] hover:border-[#FF5A36] transition-all p-6" data-testid={`partner-type-${i}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#FF5A36]/10 flex items-center justify-center">
                    <type.icon className="w-6 h-6 text-[#FF5A36]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>{type.title}</h3>
                </div>
                <p className="text-[#4A5568] text-sm mb-4">{type.desc}</p>
                <div className="space-y-2">
                  {type.features.map((f, j) => (
                    <div key={j} className="flex items-center gap-2 text-sm text-[#0A1E40]">
                      <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                      {f}
                    </div>
                  ))}
                </div>
                <Link to="/partner/register" className="block mt-5">
                  <Button className="w-full bg-[#0A1E40] hover:bg-[#0A1E40]/90 text-white rounded-xl">
                    Register as {type.title.split(' ')[0]} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4" data-testid="benefits-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0A1E40] text-center mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Partner Benefits
          </h2>
          <p className="text-[#4A5568] text-center mb-10 max-w-xl mx-auto">Everything you need to grow your visa business.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((b, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#D1CBBF] p-5 hover:shadow-md transition-all" data-testid={`benefit-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-[#FF5A36]/10 flex items-center justify-center mb-3">
                  <b.icon className="w-5 h-5 text-[#FF5A36]" />
                </div>
                <h3 className="font-bold text-[#0A1E40] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{b.title}</h3>
                <p className="text-sm text-[#4A5568] leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 bg-[#0A1E40]" data-testid="how-it-works-partner">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            How It Works
          </h2>
          <p className="text-white/60 text-center mb-10">Get started in 3 simple steps.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <div key={i} className="text-center" data-testid={`step-${i}`}>
                <div className="w-16 h-16 rounded-2xl bg-[#FF5A36] flex items-center justify-center mx-auto mb-4">
                  <span className="text-white text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.num}</span>
                </div>
                <h3 className="text-white font-bold text-lg mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</h3>
                <p className="text-white/60 text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4" data-testid="joinus-cta">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0A1E40] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Partner With Us?
          </h2>
          <p className="text-[#4A5568] mb-8">Join hundreds of partners already growing their business with EasyMyVisa.</p>
          <Link to="/partner/register">
            <Button className="bg-[#FF5A36] hover:bg-[#E0492B] text-white text-lg px-10 py-6 rounded-full font-semibold" data-testid="joinus-bottom-cta">
              Register Now <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default JoinUsPage;
